<template>
    <div class="survey" :class="`is-${layout}`">
        <div class="progress">
            <div class="measure">
                <div class="progress-card">
                    <p class="progress-label">
                        <span class="text-ink">{{ t("perspective.survey.sectionLabel", { page: currentPage, total: totalPages }) }}</span>
                        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- decorative, aria-hidden -->
                        <span aria-hidden="true">&middot;</span>
                        <span>{{ t("perspective.survey.answeredCount", { answered: answeredCount, total: totalQuestions }) }}</span>
                    </p>
                    <div class="sections">
                        <button
                            v-for="section in sections"
                            :key="section.page"
                            type="button"
                            class="section"
                            :class="{ 'is-current': section.page === currentPage }"
                            :style="{ '--questions': section.questions.length }"
                            :aria-label="t('perspective.survey.pageLabel', { page: section.page, total: totalPages })"
                            :aria-current="section.page === currentPage ? 'step' : undefined"
                            :disabled="isSubmitting || isSaving"
                            @click="goToPage(section.page)">
                            <span v-for="q in section.questions" :key="q.id" class="segment" :class="{ 'is-filled': q.id in responses }" />
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div ref="scrollContainer" class="scroll-container">
            <div :key="currentPage" class="page">
                <div class="measure flex flex-col gap-6 md:gap-0 md:divide-y md:divide-border">
                    <div
                        v-for="(q, index) in pageQuestions"
                        :key="q.id"
                        :data-question-id="q.id"
                        :class="['max-md:bg-surface-raised max-md:rounded-2xl max-md:shadow-sm max-md:px-5 max-md:py-4', 'md:py-10 md:first:pt-0']">
                        <p class="body-small text-ink mb-5 flex gap-2">
                            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- decorative, aria-hidden -->
                            <span class="tabular-nums" aria-hidden="true">{{ (currentPage - 1) * QUESTIONS_PER_PAGE + index + 1 }}.</span>
                            <span>{{ q.text }}</span>
                        </p>
                        <LikertScale
                            :model-value="responses[q.id]"
                            :disabled="isSubmitting"
                            :label="q.text"
                            @update:model-value="(v) => handleResponse(q.id, v)"
                            @advance="(via) => advanceToNextQuestion(q.id, via)" />
                    </div>
                </div>
            </div>
        </div>

        <div class="footer">
            <p v-if="validationError" role="alert" class="text-center details text-chip-status-overdue-text mb-2 measure">{{ validationError }}</p>
            <div class="measure flex flex-wrap items-center justify-center gap-3">
                <!-- Keep escape beside Back across viewport sizes; placing it above desktop questions made
                     it look like assessment instructions.
                     -->
                <slot name="escape" />
                <BaseButton v-if="currentPage > 1" type="button" theme="neutral" class="footer-button" :disabled="isSubmitting || isSaving" @click="goBack">{{
                    t("perspective.survey.backLabel")
                }}</BaseButton>
                <BaseButton
                    ref="nextButton"
                    type="button"
                    theme="primary"
                    class="footer-button whitespace-normal wrap-anywhere"
                    :disabled="isSubmitting || isSaving"
                    @click="isLastPage ? handleSubmit() : goNext()">
                    <template v-if="isLastPage">
                        <span v-if="isSubmitting">{{ t("perspective.survey.submitting") }}</span>
                        <span v-else-if="isSaving">{{ t("perspective.survey.saving") }}</span>
                        <span v-else>{{ submitLabel ?? t("perspective.survey.submit") }}</span>
                    </template>
                    <template v-else>
                        <span v-if="isSaving">{{ t("perspective.survey.saving") }}</span>
                        <span v-else>{{ t("perspective.survey.next") }}</span>
                    </template>
                </BaseButton>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
/* eslint design-system/no-off-scale-spacing: "off" -- The progress bar's merged sections overlap by 1px, because fractional segment widths leave anti-aliased seams between touching segments. */
/**
 * Share the survey across Perspectives and guided setup to prevent behavior drift. Callers control exit
 * through the escape slot and result handling through submitted.
 */
import { useActiveElement } from "@vueuse/core";
import { fetchApi } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import LikertScale from "~vue/components/LikertScale.vue";
import type { Question } from "~vue/pages/perspectiveTypes";
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

const QUESTIONS_PER_PAGE = 7;
const SAVE_RETRY_DELAYS_MS = [300, 800];
const SAVE_ATTEMPTS = 3;

const {
    questions,
    savedResponses,
    sessionId,
    layout = "page",
    submitLabel,
    syncUrl = false,
} = defineProps<{
    questions: Question[];
    savedResponses: Record<string, number>;
    sessionId: string;
    /**
     * page uses internal question scrolling and a pinned footer; embedded uses the host's scrolling for
     * surveys within longer pages.
     */
    layout?: "page" | "embedded";
    submitLabel?: string;
    /** Write the current page into `?page=` so browser back and a refresh land where the user was. */
    syncUrl?: boolean;
}>();

const emit = defineEmits<{ submitted: [payload: unknown] }>();
const { t } = useI18n();

/* Held as a reason, not text, so the message follows a language switch and a save failure can be cleared by kind. */
type ValidationProblem = { kind: "unansweredBeforeContinuing" | "unansweredBeforeSubmitting"; remaining: number } | { kind: "submitFailed" | "saveFailed" };

const responses = ref<Record<string, number>>({ ...savedResponses });
const currentPage = ref(1);
const validationProblem = ref<ValidationProblem | null>(null);
const validationError = computed(() => {
    const problem = validationProblem.value;
    switch (problem?.kind) {
        case "unansweredBeforeContinuing":
            return t("perspective.survey.unansweredBeforeContinuing", { count: problem.remaining }, problem.remaining);
        case "unansweredBeforeSubmitting":
            return t("perspective.survey.unansweredBeforeSubmitting", { count: problem.remaining }, problem.remaining);
        case "submitFailed":
            return t("perspective.errors.submitFailed");
        case "saveFailed":
            return t("perspective.errors.saveFailed");
        default:
            return null;
    }
});
const highlightedUnanswered = ref<Set<string>>(new Set());
const isSubmitting = ref(false);
const isSaving = ref(false);

const scrollContainer = useTemplateRef<HTMLElement>("scrollContainer");
const nextButton = useTemplateRef("nextButton");

/*
 * Per-question versions prevent stale retries from overwriting newer answers. Failed saves block submission
 * because scoring uses persisted answers.
 */
const pendingSaves = new Map<string, Promise<boolean>>();
const saveAborts = new Map<string, AbortController>();
const saveVersions = ref<Record<string, number>>({});
const failedSaves = ref<Set<string>>(new Set());

const totalQuestions = computed(() => questions.length);
const totalPages = computed(() => Math.max(1, Math.ceil(totalQuestions.value / QUESTIONS_PER_PAGE)));
const answeredCount = computed(() => questions.filter((q) => q.id in responses.value).length);
const sections = computed(() =>
    Array.from({ length: totalPages.value }, (_, i) => {
        const sectionQuestions = questions.slice(i * QUESTIONS_PER_PAGE, (i + 1) * QUESTIONS_PER_PAGE);
        return { page: i + 1, questions: sectionQuestions };
    }),
);
const isLastPage = computed(() => currentPage.value >= totalPages.value);

const pageQuestions = computed(() => {
    const start = (currentPage.value - 1) * QUESTIONS_PER_PAGE;
    return questions.slice(start, start + QUESTIONS_PER_PAGE);
});

const activeElement = useActiveElement();
const focusedId = computed(() => {
    const focusedQuestion = activeElement.value?.closest("[data-question-id]");
    if (focusedQuestion) {
        return focusedQuestion.getAttribute("data-question-id");
    }
    return pageQuestions.value.find((q) => !(q.id in responses.value))?.id ?? null;
});

function computeResumePage(): number {
    for (let p = 1; p <= totalPages.value; p++) {
        const start = (p - 1) * QUESTIONS_PER_PAGE;
        const qs = questions.slice(start, start + QUESTIONS_PER_PAGE);
        if (qs.some((q) => !(q.id in responses.value))) {
            return p;
        }
    }
    return totalPages.value;
}

function resolveInitialPage(): number {
    const maxAllowed = computeResumePage();
    if (!syncUrl) {
        return maxAllowed;
    }
    const raw = new URLSearchParams(globalThis.location.search).get("page");
    if (raw === null) {
        return maxAllowed;
    }
    const parsed = parseInt(raw, 10);
    if (isNaN(parsed)) {
        return maxAllowed;
    }
    return Math.max(1, Math.min(parsed, maxAllowed));
}

currentPage.value = resolveInitialPage();

function writePageToUrl(page: number): void {
    if (!syncUrl) {
        return;
    }
    const url = new URL(globalThis.location.href);
    url.searchParams.set("page", String(page));
    globalThis.history.replaceState({}, "", `${url.pathname}${url.search}`);
}

if (syncUrl) {
    const urlPage = new URLSearchParams(globalThis.location.search).get("page");
    if (urlPage !== null && parseInt(urlPage, 10) !== currentPage.value) {
        writePageToUrl(currentPage.value);
    }
}

/* Reopening can return updated answers without changing the session ID, so watch the response object. */
watch(
    () => savedResponses,
    () => {
        responses.value = { ...savedResponses };
        for (const controller of saveAborts.values()) {
            controller.abort();
        }
        saveAborts.clear();
        pendingSaves.clear();
        saveVersions.value = {};
        failedSaves.value = new Set();
        validationProblem.value = null;
    },
);

function scrollToFocused() {
    if (!focusedId.value) {
        return;
    }
    scrollContainer.value?.querySelector(`[data-question-id="${focusedId.value}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
}

function advanceToNextQuestion(fromQuestionId: string, via: "pointer" | "keyboard") {
    const pageQs = pageQuestions.value;
    const toIndex = pageQs.findIndex((q) => q.id === fromQuestionId) + 1;

    if (toIndex >= pageQs.length) {
        if (via === "keyboard") {
            nextButton.value?.$el.focus();
        }
        return;
    }

    const targetEl = scrollContainer.value?.querySelector(`[data-question-id="${pageQs[toIndex]!.id}"]`);
    if (!targetEl) {
        return;
    }

    if (via === "keyboard") {
        targetEl.querySelector<HTMLElement>('[role="slider"]')?.focus();
    }
    targetEl.scrollIntoView({ behavior: "smooth", block: "center" });
}

onMounted(async () => {
    await nextTick();
    scrollToFocused();
});

watch(focusedId, async (id) => {
    if (!id) {
        return;
    }
    await nextTick();
    scrollToFocused();
});

function handleResponse(questionId: string, value: number) {
    responses.value = { ...responses.value, [questionId]: value };
    highlightedUnanswered.value = new Set([...highlightedUnanswered.value].filter((id) => id !== questionId));
    if (highlightedUnanswered.value.size === 0 && failedSaves.value.size === 0) {
        validationProblem.value = null;
    }
    queueSave(questionId, value);
}

/** Staff autofill entry point; does not persist answers. */
function adoptResponses(filled: Record<string, number>) {
    responses.value = { ...responses.value, ...filled };
    clearValidation();
    navigateToPage(totalPages.value);
}

defineExpose({ adoptResponses });

function validateCurrentPage(): boolean {
    const unanswered = pageQuestions.value.filter((q) => !(q.id in responses.value));
    if (unanswered.length === 0) {
        return false;
    }
    validationProblem.value = { kind: "unansweredBeforeContinuing", remaining: unanswered.length };
    highlightedUnanswered.value = new Set(unanswered.map((q) => q.id));
    scrollToFirstUnanswered();
    return true;
}

function clearValidation() {
    validationProblem.value = null;
    highlightedUnanswered.value = new Set();
}

async function navigateToPage(page: number) {
    currentPage.value = page;
    writePageToUrl(page);
    await nextTick();
    const firstQ = scrollContainer.value?.querySelector("[data-question-id]");
    if (firstQ) {
        firstQ.scrollIntoView({ block: "start" });
        firstQ.querySelector<HTMLElement>('[role="slider"]')?.focus();
    }
}

async function goNext() {
    if (validateCurrentPage()) {
        return;
    }
    if (await ensurePagePersisted()) {
        return;
    }
    clearValidation();
    navigateToPage(currentPage.value + 1);
}

function goBack() {
    clearValidation();
    navigateToPage(currentPage.value - 1);
}

async function goToPage(page: number) {
    if (page === currentPage.value) {
        return;
    }
    if (page > currentPage.value) {
        page = Math.min(page, computeResumePage());
        if (page === currentPage.value) {
            return;
        }
        if (validateCurrentPage()) {
            return;
        }
        if (await ensurePagePersisted()) {
            return;
        }
    }
    clearValidation();
    navigateToPage(page);
}

async function handleSubmit() {
    const allUnanswered = questions.filter((q) => !(q.id in responses.value));
    if (allUnanswered.length > 0) {
        const targetPage = Math.floor(questions.indexOf(allUnanswered[0]!) / QUESTIONS_PER_PAGE) + 1;
        if (targetPage !== currentPage.value) {
            navigateToPage(targetPage);
            return;
        }
        validationProblem.value = { kind: "unansweredBeforeSubmitting", remaining: allUnanswered.length };
        highlightedUnanswered.value = new Set(allUnanswered.map((q) => q.id));
        scrollToFirstUnanswered();
        return;
    }

    if (await ensureAllPersisted()) {
        return;
    }

    isSubmitting.value = true;
    try {
        const response = await fetchApi(`/v1/perspectives/sessions/${sessionId}/submit`, { method: "POST" });
        if (!response.ok) {
            validationProblem.value = { kind: "submitFailed" };
            return;
        }
        emit("submitted", await response.json().catch(() => null));
    } catch {
        validationProblem.value = { kind: "submitFailed" };
    } finally {
        isSubmitting.value = false;
    }
}

function queueSave(questionId: string, value: number): void {
    const version = (saveVersions.value[questionId] ?? 0) + 1;
    saveVersions.value = { ...saveVersions.value, [questionId]: version };

    /* Cancel superseded saves to reduce the risk of an older PUT overwriting the latest answer. */
    saveAborts.get(questionId)?.abort();
    const controller = new AbortController();
    saveAborts.set(questionId, controller);

    const request = saveWithRetry(questionId, value, version, controller.signal);
    pendingSaves.set(questionId, request);
    void request.finally(() => {
        if (pendingSaves.get(questionId) === request) {
            pendingSaves.delete(questionId);
        }
        if (saveAborts.get(questionId) === controller) {
            saveAborts.delete(questionId);
        }
    });
}

function isSuperseded(questionId: string, version: number): boolean {
    return (saveVersions.value[questionId] ?? 0) !== version;
}

async function saveWithRetry(questionId: string, value: number, version: number, signal: AbortSignal): Promise<boolean> {
    for (let attempt = 0; attempt < SAVE_ATTEMPTS; attempt++) {
        // Check before retrying so an obsolete answer cannot overwrite a newer one.
        if (isSuperseded(questionId, version)) {
            return false;
        }
        try {
            const response = await fetchApi(`/v1/perspectives/sessions/${sessionId}/responses/${questionId}`, {
                method: "PUT",
                body: JSON.stringify({ value }),
                signal,
            });
            if (response.ok) {
                /*
                 * An older request can succeed after the answer changes; it must not clear the newer
                 * answer's failure flag.
                 */
                if (isSuperseded(questionId, version)) {
                    return false;
                }
                markSaved(questionId);
                return true;
            }
        } catch {
            // A single failed attempt is not a failed save; the loop decides.
        }
        if (isSuperseded(questionId, version)) {
            return false;
        }
        const delay = SAVE_RETRY_DELAYS_MS[attempt];
        if (delay !== undefined) {
            await new Promise((resolve) => setTimeout(resolve, delay));
        }
    }

    failedSaves.value = new Set(failedSaves.value).add(questionId);
    return false;
}

function markSaved(questionId: string): void {
    if (!failedSaves.value.has(questionId)) {
        return;
    }
    const next = new Set(failedSaves.value);
    next.delete(questionId);
    failedSaves.value = next;
    if (next.size === 0 && validationProblem.value?.kind === "saveFailed") {
        validationProblem.value = null;
    }
}

async function ensurePagePersisted(): Promise<boolean> {
    isSaving.value = true;
    try {
        const ids = pageQuestions.value.map((q) => q.id);
        await Promise.allSettled(ids.map((id) => pendingSaves.get(id)).filter(Boolean) as Promise<boolean>[]);
        if (ids.some((id) => failedSaves.value.has(id))) {
            validationProblem.value = { kind: "saveFailed" };
            return true;
        }
        return false;
    } finally {
        isSaving.value = false;
    }
}

async function ensureAllPersisted(): Promise<boolean> {
    isSaving.value = true;
    try {
        await Promise.allSettled([...pendingSaves.values()]);
        if (failedSaves.value.size === 0) {
            return false;
        }
        const targetPage = Math.floor(questions.findIndex((q) => failedSaves.value.has(q.id)) / QUESTIONS_PER_PAGE) + 1;
        if (targetPage !== currentPage.value) {
            navigateToPage(targetPage);
        }
        validationProblem.value = { kind: "saveFailed" };
        return true;
    } finally {
        isSaving.value = false;
    }
}

function scrollToFirstUnanswered() {
    const firstId = [...highlightedUnanswered.value][0];
    if (firstId) {
        scrollContainer.value?.querySelector(`[data-question-id="${firstId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .survey {
        @apply relative flex flex-col;
    }

    .survey.is-page {
        @apply grow overflow-clip max-h-full;
    }

    .survey.is-page .scroll-container {
        @apply grow overflow-y-auto overflow-x-hidden min-h-0;
        /*
         * iOS WKWebView fails to repaint the scroll container after Vue's :key-driven
         * DOM swap on page navigation, leaving a blank view. Promoting the element to
         * its own compositing layer forces a repaint.
         */
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;
    }

    /*
     * Embedded surveys share the host page's scrolling. Keep the footer in normal flow so it does not
     * overlay other page content.
     */
    .survey.is-embedded .progress {
        @apply sticky top-0 z-10 bg-surface;
    }

    .progress {
        /* One step less on top: the label's line height already adds a few pixels above the text. */
        @apply shrink-0 border-b border-border px-6 pt-3 pb-3;
    }

    .progress-card {
        @apply max-md:bg-surface-raised max-md:rounded-full max-md:shadow-sm max-md:px-5 max-md:pt-2 max-md:pb-3;
    }

    .progress-label {
        @apply mb-2 flex gap-2 scaffold-details-small text-ink-muted tabular-nums;
    }

    /* Square segments and green, not magenta, so the bar never reads as one more slider to drag. */
    .sections {
        @apply flex gap-2;
    }

    /*
     * Every section keeps one segment per question; outside the current section the gaps and inner corners
     * close, so a section merges into a solid block, and splits back into squares, by transition.
     */
    /* The padding gives each section a 26px target; the negative margin keeps the bar's own height. */
    .section {
        @apply flex basis-0 gap-0 -my-1 py-2 cursor-pointer disabled:cursor-default;
        flex-grow: var(--questions);
        transition:
            gap var(--motion-duration-moderate) var(--motion-ease-standard),
            flex-grow var(--motion-duration-moderate) var(--motion-ease-standard);
    }

    /* On a phone an equal share leaves the current section's squares as hairlines, so it takes a bigger share there. */
    .section.is-current {
        @apply gap-1 max-md:grow-[calc(var(--questions)*3)];
    }

    .segment {
        @apply relative h-2.5 flex-1 overflow-hidden rounded-none bg-survey-progress-track first:rounded-l-xs last:rounded-r-xs;
        transition:
            border-radius var(--motion-duration-moderate) var(--motion-ease-standard),
            margin var(--motion-duration-moderate) var(--motion-ease-standard);
    }

    .section.is-current .segment {
        @apply rounded-xs;
    }

    .section:not(.is-current) .segment + .segment {
        margin-left: -1px;
    }

    .segment.is-filled::after {
        @apply absolute inset-0 origin-left bg-survey-progress-fill content-[''];
        animation: segment-fill var(--motion-duration-moderate) cubic-bezier(0.22, 1, 0.36, 1) both;
    }

    .section.is-current .segment.is-filled {
        animation: segment-pop var(--motion-duration-moderate) ease-out;
    }

    @keyframes segment-fill {
        from {
            scale: 0 1;
        }
    }

    @keyframes segment-pop {
        40% {
            scale: 1.08;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .section,
        .segment,
        .segment::after {
            animation: none !important;
            transition: none !important;
        }
    }

    /* Padding lives here rather than in utilities so an embedding host can strip it, as it does for the progress bar. */
    .page {
        @apply px-6 py-10;
    }

    .measure {
        @apply max-w-2xl mx-auto;
    }

    .footer {
        @apply shrink-0 border-t border-border px-6 py-3;
    }

    .footer-button {
        @apply min-w-36 max-w-full;
    }
}
</style>
